import { get, merge } from 'lodash';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS, HTTP_RESPONSE_STATUS } from 'constants/common';
import { getVisibleItems } from 'resolvers/getVisibleEntities';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc AuthFlowMemberVerificationMatchCriteria defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { AuthFlowMemberVerificationMatchCriteriaService } from 'services/authFlowMemberVerificationMatchCriteria';
 *
 * // create service instance and send/cancel request
 * const authFlowMemberVerificationMatchCriteriaService = new AuthFlowMemberVerificationMatchCriteriaService();
 * const result = await authFlowMemberVerificationMatchCriteriaService.fetchData( '<user-id>' ); // fetch data
 * authFlowMemberVerificationMatchCriteriaService.cancel(); // cancel an active request
 */
export class AuthFlowMemberVerificationMatchCriteriaService extends HttpService {

    // make request and fetch component data
    async fetchComponentData( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callerType,
                    },
                } ),
            } );

            // extract data
            const visibility = get( response, 'body.component.data.state.fieldVisibility', {} );
            const criteria = get( response, 'body.component.structure.criteria', [] );
            const values = get( response, 'body.component.data.state.values', [] );
            const mandatoryCriteria = get( response, 'body.component.structure.mandatoryCriteria', [] );
            const optionalCriteria = get( response, 'body.component.structure.optionalCriteria', [] );
            const actions = get( response, 'body.component.structure.actions', [] );
            const minOptionalCriteriaMatch = get( response, 'body.component.data.state.config.minOptionalCriteriaMatch', null );
            const actionVisibility = get( response, 'body.component.data.state.actionVisibility', {} );
            const disabledFields = get( response, 'body.component.data.state.disabledFields', {} );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    criteria: getVisibleItems( criteria, visibility ),
                    values,
                    mandatoryCriteria,
                    optionalCriteria,
                    actions,
                    minOptionalCriteriaMatch,
                    actionVisibility,
                    disabledFields
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // update match criteria state
    async saveCriteriaSelection( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callerType,
                    },
                } ),
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: null,
            };
        } catch( error ) {
            if( HTTP_RESPONSE_STATUS.NONE === get( error, 'data.status', 0 ) ) {
                return {
                    status: SERVICE_RESPONSE_STATUS.CANCELLED,
                    data: null,
                };
            } else {
                return {
                    status: SERVICE_RESPONSE_STATUS.FAILURE,
                    data: null,
                };
            }
        }
    }
}
